export const getErrorMessage = (data: any) => {
  try {
    let response = data.request.response;
    return JSON.parse(response).message;
  } catch (error) {
    return data.message || "An unexpected error occurred";
  }
};

/**
 * The API's own message for a failed request, or undefined when the failure
 * carries none — a network error, an aborted request, a non-JSON body.
 *
 * Unlike getErrorMessage this never substitutes Axios' generic text, so a
 * caller can tell "the server explained what went wrong" apart from "the
 * request simply failed" and offer its own recovery hint in the second case.
 */
export const getApiErrorMessage = (error: unknown): string | undefined => {
  const response = (error as { request?: { response?: unknown } } | null)
    ?.request?.response;
  if (typeof response !== "string") return undefined;

  let message: unknown;
  try {
    ({ message } = JSON.parse(response) as { message?: unknown });
  } catch {
    return undefined;
  }

  if (typeof message === "string") return message || undefined;
  // Nest's validation pipe returns one message per failed constraint.
  if (Array.isArray(message)) {
    return (
      message.filter((part) => typeof part === "string").join(" ") || undefined
    );
  }
  return undefined;
};

/**
 * HTTP status from an Axios-shaped error, or undefined when the request never
 * got a response (a network error, a CORS rejection, an aborted request).
 */
export const getErrorStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null | undefined)?.response
    ?.status;

/**
 * Default query retry policy. 401 and 403 are terminal — the request is
 * unauthorised, not flaky — so retrying only multiplies the failure: three
 * extra requests and three extra unhandled rejections per attempt, all landing
 * in one Error Tracking bucket.
 */
export const retryUnlessUnauthorized = (
  failureCount: number,
  error: unknown,
): boolean => {
  const status = getErrorStatus(error);
  if (status === 401 || status === 403) return false;
  return failureCount < 3;
};
